import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import * as browser from 'lib/Browser';
import {LEGACY_FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_LIGHT, GRAY, GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as keyboard from 'util/Keyboard';
import {InputChangeEvent} from 'util/Types';

export enum ClearType {
  // Allow clearing input with an "X" button.
  X = 'X',
  // Allow clearing input with a "Cancel" button.
  CANCEL = 'CANCEL',
}

export enum BorderRadius {
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export interface PlainSearchInputProps {
  className?: string;
  borderRadius: BorderRadius;
  // If unspecified, neither way of clearing the input will be shown.
  clearType?: ClearType;
  shouldAutoSelect: boolean;
  value?: string | null;
  onChange: (e: InputChangeEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onEscape: () => void;
  onBlur?: () => void;
  placeholder: string;
  additionalIcons?: Array<JSX.Element>;
  spellCheck?: boolean;
  disabled?: boolean;
  // Optional content to render between the search icon and the input field. If passed, the magnifiying glass
  // icon will not be shown, even if renderBeforeInput returns a null value.
  renderBeforeInput?: () => JSX.Element | null;
}

export interface PlainSearchInputState {
  cancelButtonHover: boolean;
  focus: boolean;
}

/**
 * Text input that provides some additional search-related features:
 * - Show a magnifying glass icon when no text has been entered.
 * - Allow clearing the contents with the escape key or a button.
 */
export default class PlainSearchInput extends React.Component<PlainSearchInputProps, PlainSearchInputState> {
  static defaultProps = {
    borderRadius: BorderRadius.MEDIUM,
    shouldAutoSelect: false,
    onEscape: (): void => {},
    spellCheck: true,
  };

  constructor(props: PlainSearchInputProps) {
    super(props);
    this.state = {
      cancelButtonHover: false,
      focus: props.shouldAutoSelect,
    };
  }

  input = React.createRef<HTMLInputElement>();

  componentDidMount(): void {
    if (this.props.shouldAutoSelect && browser.isBrowser) {
      // The input component is sometimes not rendered at this point, so defer setting focus.
      _.defer(() => {
        if (this.input.current) {
          this.input.current.focus();
          this.input.current.select();
        }
      });
    }
  }

  focus(): void {
    if (this.input.current) {
      this.input.current.focus();
    }
  }

  render(): JSX.Element {
    return (
      <div className={classNames(this.props.className, css(styles.plainSearchInputOuterWrapper))}>
        <div
          className={classNames(css(styles.plainSearchInputInnerWrapper), {
            [css(styles.plainSearchInputInnerWrapperFocus)]: this.state.focus,
            [css(styles.borderRadiusLarge)]: this.props.borderRadius === BorderRadius.LARGE,
            [css(styles.plainSearchInputInnerWrapperDisabled)]: this.props.disabled,
          })}
        >
          {!this.props.value && !this.props.renderBeforeInput && (
            <BenchlingIcon
              className={classNames(css(styles.plainSearchInputIcon), {
                [css(styles.plainSearchInputIconHidden)]: this.state.focus,
              })}
              icon="search"
            />
          )}
          {this.props.renderBeforeInput?.() ?? null}
          <input
            // Note that we don't want search because ESC will clear input.
            type="text"
            disabled={this.props.disabled}
            className={classNames(css(styles.plainSearchInput))}
            placeholder={this.props.placeholder}
            ref={this.input}
            value={this.props.value || ''}
            onFocus={() => {
              this.setState({focus: true});
            }}
            onBlur={() => {
              this.setState({focus: false});
              if (this.props.onBlur) {
                this.props.onBlur();
              }
            }}
            onChange={this.props.onChange}
            onKeyDown={this.props.onKeyDown}
            onKeyUp={(e) => {
              if (keyboard.isEscapeKey(e)) {
                this.props.onEscape();
              }
            }}
            {...getTestProps({component: 'PlainSearchInput', element: 'input'})}
            spellCheck={this.props.spellCheck}
          />
          {
            // Show cancel button if non-null query to allow users to exit search mode.
            this.props.value != null && this.props.clearType === ClearType.CANCEL && (
              <a
                className={classNames(css(styles.cancelButton), {
                  [css(styles.cancelButtonHover)]: Boolean(this.state.cancelButtonHover),
                })}
                onClick={this.props.onEscape}
                onMouseOver={() => {
                  this.setState({cancelButtonHover: true});
                }}
                onMouseOut={() => {
                  this.setState({cancelButtonHover: false});
                }}
              >
                Cancel
              </a>
            )
          }
          {this.props.additionalIcons != null && this.props.additionalIcons.map((icon) => icon)}
        </div>
        {this.props.value && this.props.clearType === ClearType.X && (
          <BenchlingIcon
            className={classNames(css(styles.xButton))}
            icon="close"
            onClick={this.props.onEscape}
          />
        )}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  borderRadiusLarge: {
    borderRadius: 3,
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    ...LEGACY_FONT_PROXIMA_13,
    paddingLeft: '12px',
    paddingRight: '4px',
  },
  cancelButtonHover: {
    textDecoration: 'none',
  },
  plainSearchInput: {
    minHeight: '26px',
    height: '26px',
    border: 'none',
    minWidth: '10px',
  },
  plainSearchInputIcon: {
    color: GRAY_DARK,
    flex: '0 0 12px',
    marginLeft: '4px',
    height: '16px',
  },
  plainSearchInputIconHidden: {
    display: 'none',
  },
  plainSearchInputInnerWrapperFocus: {
    border: `1px solid ${BLUE_LIGHT}`,
  },
  plainSearchInputInnerWrapper: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    border: `1px solid ${GRAY}`,
    borderRadius: '2px',
    height: '28px',
    backgroundColor: 'white',
  },
  plainSearchInputInnerWrapperDisabled: {
    backgroundColor: GRAY_LIGHT,
  },
  plainSearchInputOuterWrapper: {
    position: 'relative',
    display: 'flex',
  },
  xButton: {
    // Specify a positive z-index so the button doesn't get covered up by the text input.
    zIndex: 100,
    marginLeft: '5px',
    marginRight: '10px',
    alignSelf: 'center',
    cursor: 'pointer',
    flex: '0 0 9px',
  },
});
